import { Card, Col, Form, Row, Select, Table, notification } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { coreApi } from "../../../api/calls";
import { fetchCityBranches } from "../../../api/campaignsApi";

const createEmptyAggregate = () => {
  const aggregate = {
    key: "aggregate",
    total_count: 0,
    total_sum: 0,
  };

  for (let month = 1; month <= 12; month++) {
    aggregate[`count_${month}`] = 0;
    aggregate[`sum_${month}`] = 0;
  }

  return aggregate;
};

const TerminatedBuildingsByTagsReport = ({ year }) => {
  const { t } = useTranslation();

  const [isLoading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [rows, setRows] = useState([]);
  const [aggregate, setAggregate] = useState(createEmptyAggregate());
  const [currency, setCurrency] = useState("CZK");
  const [selectedCityIds, setSelectedCityIds] = useState([]);

  useEffect(() => {
    fetchCityBranches()
      .then(setCities)
      .catch(() => notification.error({ message: "Error" }));
  }, []);

  useEffect(() => {
    if (!year) return;

    setLoading(true);

    coreApi
      .get("/reports/country/terminated-buildings-by-tags", {
        params: {
          year: year.format("YYYY-MM-DD"),
          city_branch_ids: selectedCityIds.length ? selectedCityIds : null,
        },
      })
      .then(({ data }) => {
        setRows(data?.rows || []);
        setAggregate(data?.aggregate || createEmptyAggregate());
        setCurrency(data?.currency_iso_4217 || "CZK");
      })
      .catch((err) => notification.error({ message: err?.response?.data?.message }))
      .finally(() => {
        setLoading(false);
      });
  }, [year, selectedCityIds]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("cs-CZ", {
      style: "currency",
      currency: currency || "CZK",
    }).format(Number(value || 0));

  const columns = [
    {
      title: t("reports.country.terminationReason"),
      key: "tag_name",
      dataIndex: "tag_name",
      fixed: "left",
      width: 240,
      render: (_, row) => (row.is_missing_tag ? t("reports.country.missingTag") : row.tag_name),
      sorter: (left, right) => {
        if (left.is_missing_tag !== right.is_missing_tag) {
          return left.is_missing_tag ? 1 : -1;
        }

        return (left.tag_name || "").localeCompare(right.tag_name || "");
      },
    },
    ...Array.from({ length: 12 }, (_, index) => {
      const month = index + 1;

      return {
        title: moment().month(index).format("MMMM"),
        children: [
          {
            title: t("reports.country.count"),
            key: `count_${month}`,
            dataIndex: `count_${month}`,
            width: 100,
            align: "right",
            render: (value) => Number(value || 0),
          },
          {
            title: t("reports.country.sum"),
            key: `sum_${month}`,
            dataIndex: `sum_${month}`,
            width: 130,
            align: "right",
            render: (value) => formatCurrency(value),
          },
        ],
      };
    }),
    {
      title: t("common.total"),
      children: [
        {
          title: t("reports.country.count"),
          key: "total_count",
          dataIndex: "total_count",
          width: 110,
          align: "right",
          fixed: "right",
          render: (value) => Number(value || 0),
        },
        {
          title: t("reports.country.sum"),
          key: "total_sum",
          dataIndex: "total_sum",
          width: 140,
          align: "right",
          fixed: "right",
          render: (value) => formatCurrency(value),
        },
      ],
    },
  ];

  const renderSummary = () => (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0}>
        <strong>{t("common.total")}</strong>
      </Table.Summary.Cell>
      {Array.from({ length: 12 }, (_, index) => {
        const month = index + 1;

        return (
          <React.Fragment key={month}>
            <Table.Summary.Cell index={month * 2 - 1} align="right">
              <strong>{Number(aggregate[`count_${month}`] || 0)}</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={month * 2} align="right">
              <strong>{formatCurrency(aggregate[`sum_${month}`])}</strong>
            </Table.Summary.Cell>
          </React.Fragment>
        );
      })}
      <Table.Summary.Cell index={25} align="right">
        <strong>{Number(aggregate.total_count || 0)}</strong>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={26} align="right">
        <strong>{formatCurrency(aggregate.total_sum)}</strong>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Form layout="inline">
          <Form.Item label={t("common.cityBranch")}>
            <Select
              mode="multiple"
              allowClear
              style={{ minWidth: 280 }}
              placeholder={t("expansion.selectCities")}
              value={selectedCityIds}
              onChange={setSelectedCityIds}
              filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
              optionLabelProp="label"
              showSearch
            >
              {cities.map((city) => (
                <Select.Option key={city.id} value={city.id} label={city.name}>
                  {city.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Col>
      <Col span={24}>
        <Card loading={isLoading} size="small" bodyStyle={{ padding: "10px" }}>
          <Table
            loading={isLoading}
            size="small"
            rowKey="key"
            dataSource={rows}
            columns={columns}
            pagination={false}
            sticky={true}
            scroll={{ y: "calc(100vh - 240px)", x: "max-content" }}
            summary={renderSummary}
          />
        </Card>
      </Col>
    </Row>
  );
};

export default TerminatedBuildingsByTagsReport;
